import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Plus, Trash2, Pencil, Loader2, Save, Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { adminResourcesApi } from "../../lib/api";
import { Modal, DInput, DSelect, DTextarea } from "./ui";

const EMPTY = {
  slug: "", category: "blog", title: "", seoTitle: "", metaDescription: "", h1: "",
  content: "", excerpt: "", featuredImage: "", authorName: "ServiceGuru Pro Team",
  authorBio: "", tags: "", published: false, indexable: true,
};

/** Super-admin CRUD for /resources/blog|guides|tools articles — content
 * editors don't need code access to publish or update SEO copy. */
export default function SeoResourcesPanel() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // article obj, or {} for new
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      setArticles(await adminResourcesApi.list());
      adminResourcesApi.seoAudit().then(setAudit).catch(() => {});
    } catch (e) {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (a) => setEditing({ ...a, tags: (a.tags || []).join(", ") });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, tags: editing.tags ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
      if (editing.id) {
        const { id, createdAt, updatedAt, publishedAt, ...patch } = payload;
        await adminResourcesApi.update(editing.id, patch);
        toast.success("Article updated");
      } else {
        const { id, createdAt, updatedAt, publishedAt, ...create } = payload;
        await adminResourcesApi.create(create);
        toast.success("Article created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await adminResourcesApi.remove(id);
      toast.success("Article deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const togglePublish = async (a) => {
    try {
      await adminResourcesApi.update(a.id, { published: !a.published });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="seo-resources-panel">
        <Loader2 className="h-6 w-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="seo-resources-panel">
      {audit && (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="seo-audit-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <h3 className="text-[13.5px] font-semibold text-slate-100">SEO Health</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <AuditStat label="Indexable pages" value={audit.total_indexable_pages} />
            <AuditStat label="Published articles" value={audit.articles.published} />
            <AuditStat label="Draft articles" value={audit.articles.drafts} />
            <AuditStat label="Feature / Industry / Solution pages" value={`${audit.template_pages.features} / ${audit.template_pages.industries} / ${audit.template_pages.solutions}`} />
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-[11.5px]">
            <span className="inline-flex items-center gap-1 text-emerald-300"><ShieldCheck className="h-3 w-3" /> Sitemap: {audit.sitemap_url}</span>
            <span className="inline-flex items-center gap-1 text-emerald-300"><ShieldCheck className="h-3 w-3" /> Robots.txt: {audit.robots_url}</span>
            {[
              ["missing_seo_title", "SEO title"],
              ["missing_meta_description", "meta description"],
              ["missing_h1", "H1"],
              ["missing_featured_image", "featured image"],
            ].map(([key, label]) =>
              audit.articles[key]?.length > 0 ? (
                <span key={key} className="inline-flex items-center gap-1 text-amber-300">
                  <AlertTriangle className="h-3 w-3" /> {audit.articles[key].length} article(s) missing {label}
                </span>
              ) : null
            )}
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-lg bg-cyan-500/15 text-cyan-300 flex items-center justify-center">
            <Newspaper className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-[15px] font-semibold text-slate-100">SEO Resources — Blog / Guides / Tools</h2>
            <p className="text-[12.5px] text-slate-400">{articles.length} article{articles.length === 1 ? "" : "s"}</p>
          </div>
        </div>
        <button
          onClick={openNew}
          data-testid="seo-resources-add"
          className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-cyan-500/20 border border-cyan-500/30 text-cyan-200 hover:bg-cyan-500/30 text-[13px] font-semibold"
        >
          <Plus className="h-4 w-4" /> New article
        </button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {articles.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-[13px]">No articles yet. Click "New article" to create one.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {articles.map((a) => (
              <div key={a.id} className="p-3.5 flex items-center gap-3" data-testid={`seo-article-row-${a.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13.5px] font-semibold text-slate-100 truncate">{a.title}</span>
                    <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{a.category}</span>
                  </div>
                  <div className="text-[12px] text-slate-500 truncate">/resources/{a.category === "blog" ? "blog" : a.category === "guide" ? "guides" : "tools"}/{a.slug}</div>
                </div>
                <button
                  onClick={() => togglePublish(a)}
                  data-testid={`seo-article-toggle-${a.slug}`}
                  className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium border ${a.published ? "border-emerald-500/30 text-emerald-300 bg-emerald-500/10" : "border-white/10 text-slate-400"}`}
                >
                  {a.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {a.published ? "Published" : "Draft"}
                </button>
                <button onClick={() => openEdit(a)} data-testid={`seo-article-edit-${a.slug}`} className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-200">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => remove(a.id)} data-testid={`seo-article-delete-${a.slug}`} className="p-2 rounded-md text-slate-400 hover:bg-white/5 hover:text-rose-300">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit article" : "New article"} onClose={() => setEditing(null)} testid="seo-article-modal">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <DInput label="Slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} testid="seo-article-slug" placeholder="how-to-choose-a-crm" />
              <DSelect label="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} options={[["blog", "Blog"], ["guide", "Guide"], ["tool", "Tool"]]} testid="seo-article-category" />
            </div>
            <DInput label="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} testid="seo-article-title" />
            <DInput label="H1" value={editing.h1} onChange={(e) => setEditing({ ...editing, h1: e.target.value })} testid="seo-article-h1" />
            <DInput label="SEO title (browser tab)" value={editing.seoTitle} onChange={(e) => setEditing({ ...editing, seoTitle: e.target.value })} testid="seo-article-seotitle" />
            <DTextarea label="Meta description" value={editing.metaDescription} onChange={(e) => setEditing({ ...editing, metaDescription: e.target.value })} rows={2} testid="seo-article-metadesc" />
            <DTextarea label="Excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} rows={2} testid="seo-article-excerpt" />
            <DInput label="Featured image URL" value={editing.featuredImage || ""} onChange={(e) => setEditing({ ...editing, featuredImage: e.target.value })} testid="seo-article-image" />
            <DTextarea label="Content (## for headings, - for bullets, blank line = new paragraph)" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} rows={10} testid="seo-article-content" />
            <div className="grid grid-cols-2 gap-3">
              <DInput label="Author name" value={editing.authorName} onChange={(e) => setEditing({ ...editing, authorName: e.target.value })} testid="seo-article-author" />
              <DInput label="Tags (comma separated)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} testid="seo-article-tags" />
            </div>
            <label className="flex items-center gap-2.5 text-[13px] text-slate-300 cursor-pointer pt-1">
              <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} data-testid="seo-article-published" />
              Published (visible on the public site and sitemap)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-md border border-white/10 text-slate-300 text-[13px]">Cancel</button>
              <button
                onClick={save}
                disabled={saving || !editing.slug || !editing.title}
                data-testid="seo-article-save"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-[13px] font-semibold disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function AuditStat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/[0.02] border border-white/5 py-2.5">
      <div className="text-[17px] font-bold text-slate-100">{value}</div>
      <div className="text-[10.5px] text-slate-500 uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}
